import React, { createContext, useState, useEffect, ReactNode, useCallback, useContext, useMemo } from 'react';
import { Admin, Contact } from '../types';
import { where } from 'firebase/firestore';
import { auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { useYearContext } from './YearContext';
import {
    DEFAULT_ROLE_PERMISSIONS,
    RolePermissions,
    RolePermissionsByRole,
    mergeRolePermissions,
} from '../auth/permissions';
import {
    fetchCollection,
    addToCollection,
    updateInCollection,
    deleteFromCollection,
    setInCollection,
    COLLECTIONS
} from '../firebase/firestoreUtils';

interface AdminContextType {
    admins: Admin[];
    contacts: Contact[];
    rolePermissions: RolePermissionsByRole;
    isLoading: boolean;
    addAdmin: (admin: Omit<Admin, 'id'>) => Promise<void>;
    updateAdmin: (admin: Admin) => Promise<void>;
    deleteAdmin: (adminId: string) => Promise<void>;
    updateRolePermissions: (role: Admin['role'], permissions: RolePermissions) => Promise<void>;
    addContact: (contact: Omit<Contact, 'id'>) => Promise<void>;
    updateContact: (contact: Contact) => Promise<void>;
    deleteContact: (contactId: string) => Promise<void>;
}

// Firestore denies reads per-collection based on the caller's role (e.g. a role
// without Users read access can't fetch ADMINS), so a rejection on one settled
// promise must fall back independently rather than discarding data the caller
// does have access to.
function settledOrDefault<T>(result: PromiseSettledResult<T>, fallback: T, label: string): T {
    if (result.status === 'fulfilled') {
        return result.value;
    }
    console.error(`Failed to fetch ${label} from Firestore`, result.reason);
    return fallback;
}

export const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { selectedYear } = useYearContext();
    const [admins, setAdmins] = useState<Admin[]>([]);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [rolePermissions, setRolePermissions] = useState<RolePermissionsByRole>(DEFAULT_ROLE_PERMISSIONS);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAdminData = async (isAuthenticated: boolean) => {
            setIsLoading(true);
            try {
                if (isAuthenticated) {
                    // Use allSettled so a permission denial on one collection doesn't also
                    // discard data the caller *does* have access to — mirrors the isolated
                    // failure handling EventsContext.tsx uses for admin-only bookings.
                    const [adminsResult, contactsResult, permissionsResult] = await Promise.allSettled([
                        fetchCollection<Admin>(COLLECTIONS.ADMINS),
                        fetchCollection<Contact>(COLLECTIONS.CONTACTS, [where('year', '==', selectedYear)]),
                        fetchCollection<{ id: Admin['role']; areas: RolePermissions }>(COLLECTIONS.ROLE_PERMISSIONS)
                    ]);

                    setAdmins(settledOrDefault(adminsResult, [], 'admins'));
                    setContacts(settledOrDefault(contactsResult, [], 'contacts'));

                    if (permissionsResult.status === 'fulfilled') {
                        setRolePermissions(mergeRolePermissions(Object.fromEntries(
                            permissionsResult.value.map(doc => [doc.id, doc.areas])
                        )));
                    } else {
                        setRolePermissions(settledOrDefault(permissionsResult, DEFAULT_ROLE_PERMISSIONS, 'role permissions'));
                    }
                } else {
                    setAdmins([]);
                    setContacts([]);
                    setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
                }
            } catch (error) {
                console.error('Failed to fetch admin data from Firestore', error);
            } finally {
                setIsLoading(false);
            }
        };

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            fetchAdminData(!!user);
        });

        return () => unsubscribe();
    }, [selectedYear]);

    const addAdmin = useCallback(async (adminData: Omit<Admin, 'id'>) => {
        try {
            const newId = await addToCollection<Admin>(COLLECTIONS.ADMINS, adminData);
            setAdmins(prev => [...prev, { ...adminData, id: newId }]);
        } catch (error) {
            console.error("Error adding admin: ", error);
            throw error;
        }
    }, []);

    const updateRolePermissions = useCallback(async (role: Admin['role'], permissions: RolePermissions) => {
        try {
            await setInCollection<{ areas: RolePermissions }>(
                COLLECTIONS.ROLE_PERMISSIONS,
                role,
                { areas: permissions }
            );
            setRolePermissions(prev => mergeRolePermissions({
                ...prev,
                [role]: permissions,
            }));
        } catch (error) {
            console.error("Error updating role permissions: ", error);
            throw error;
        }
    }, []);

    const updateAdmin = useCallback(async (updatedAdminData: Admin) => {
        try {
            await updateInCollection<Admin>(COLLECTIONS.ADMINS, String(updatedAdminData.id), updatedAdminData);
            const updated = await fetchCollection<Admin>(COLLECTIONS.ADMINS);
            setAdmins(updated);
        } catch (error) {
            console.error("Error updating admin: ", error);
            throw error;
        }
    }, []);

    const deleteAdmin = useCallback(async (adminId: string) => {
        const target = admins.find(admin => admin.id === adminId);
        const signedInEmail = auth.currentUser?.email?.trim().toLowerCase();
        if (target && signedInEmail && target.email.trim().toLowerCase() === signedInEmail) {
            throw new Error('You cannot delete your own admin account.');
        }
        try {
            await deleteFromCollection(COLLECTIONS.ADMINS, adminId);
            const updated = await fetchCollection<Admin>(COLLECTIONS.ADMINS);
            setAdmins(updated);
        } catch (error) {
            console.error("Error deleting admin: ", error);
            throw error;
        }
    }, [admins]);

    const addContact = useCallback(async (contactData: Omit<Contact, 'id'>) => {
        try {
            const dataWithYear = { ...contactData, year: contactData.year || selectedYear };
            await addToCollection<Contact>(COLLECTIONS.CONTACTS, dataWithYear);
            const updated = await fetchCollection<Contact>(COLLECTIONS.CONTACTS, [where('year', '==', selectedYear)]);
            setContacts(updated);
        } catch (error) {
            console.error("Error adding contact: ", error);
            throw error;
        }
    }, [selectedYear]);

    const updateContact = useCallback(async (updatedContactData: Contact) => {
        try {
            await updateInCollection<Contact>(COLLECTIONS.CONTACTS, String(updatedContactData.id), updatedContactData);
            const updated = await fetchCollection<Contact>(COLLECTIONS.CONTACTS, [where('year', '==', selectedYear)]);
            setContacts(updated);
        } catch (error) {
            console.error("Error updating contact: ", error);
            throw error;
        }
    }, [selectedYear]);

    const deleteContact = useCallback(async (contactId: string) => {
        try {
            await deleteFromCollection(COLLECTIONS.CONTACTS, contactId);
            const updated = await fetchCollection<Contact>(COLLECTIONS.CONTACTS, [where('year', '==', selectedYear)]);
            setContacts(updated);
        } catch (error) {
            console.error("Error deleting contact: ", error);
            throw error;
        }
    }, [selectedYear]);

    const value = useMemo(() => ({
        admins,
        contacts,
        rolePermissions,
        isLoading,
        addAdmin,
        updateAdmin,
        deleteAdmin,
        updateRolePermissions,
        addContact,
        updateContact,
        deleteContact,
    }), [
        admins,
        contacts,
        rolePermissions,
        isLoading,
        addAdmin,
        updateAdmin,
        deleteAdmin,
        updateRolePermissions,
        addContact,
        updateContact,
        deleteContact,
    ]);

    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
};

export const useAdmin = () => {
    const context = useContext(AdminContext);
    if (!context) {
        throw new Error('useAdmin must be used within an AdminProvider');
    }
    return context;
};
